import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BookDemoLink, MarketingTopBar, SiteFooter } from "../../components/marketing/shared";
import MarketingNotFound from "../../components/marketing/MarketingNotFound";
import { MarketingMotion } from "../../components/marketing/MarketingMotion";
import MarketingSeo, { seoOrigin } from "../../components/marketing/MarketingSeo";
import { Skeleton, ErrorState } from "../../components/ui/primitives";
import api from "../../lib/api";
import LegalMarkdown, { extractHeadings } from "./LegalMarkdown";
import { FOUNDER_NOTE, founderNoteReady } from "./founderNote";
import { BrandCopy } from "../../components/BrandWordmarkText";
import {
  LEGAL_FALLBACKS,
  LEGAL_FALLBACK_VERSION,
  LEGAL_PATHS,
  LEGAL_SEO,
  LEGAL_TITLES,
} from "./legalContent";
import { CTA_PRIMARY_LABEL } from "../../components/marketing/navigation";

const LEGAL_CACHE_PREFIX = "vp360:published-legal:v1:";
const LEGAL_CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// TRUST-2 — the /about founder note renders from ONE authoring file, `./founderNote.js`, which
// carries the publishing procedure (real approved words, a claims-registry row for every factual
// statement, then swap the exported constant). It stays `null` until that copy exists, and the
// content-presence guard fails closed on a half-filled object, so a work-in-progress note can
// never reach production. The honesty-commitments block below has no per-person facts and ships
// independently of it.

// TRUST-2 — the honest-by-design positioning block. Every line is a statement about what this
// SITE does (all verifiable against the claims registry's ⛔ rows), never a product claim.
const HONESTY_COMMITMENTS = [
  "No invented customer counts, adoption numbers or outcome percentages.",
  "No rented logos, stock testimonials or fabricated reviews — product-grounded proof only.",
  "No certification names we do not hold, and no uptime or response-time guarantees without a signed schedule.",
  "Prices on this site derive from the same configuration that bills real schools.",
];

function validLegalDocument(doc) {
  return Boolean(
    doc
    && typeof doc.title === "string"
    && doc.title.trim().length > 0
    && doc.title.length <= 240
    && typeof doc.body === "string"
    && doc.body.trim().length > 0
    && doc.body.length <= 250_000
  );
}

function legalCacheKey(slug) {
  return `${LEGAL_CACHE_PREFIX}${encodeURIComponent(String(slug || ""))}`;
}

export function readCachedLegalDocument(slug, now = Date.now()) {
  if (typeof window === "undefined") return null;
  try {
    const cached = JSON.parse(window.localStorage.getItem(legalCacheKey(slug)) || "null");
    if (
      !cached
      || cached.slug !== slug
      || !Number.isFinite(cached.saved_at)
      || now - cached.saved_at > LEGAL_CACHE_MAX_AGE_MS
      || !validLegalDocument(cached.doc)
    ) return null;
    return { ...cached.doc, is_cached: true, cached_at: cached.saved_at };
  } catch {
    return null;
  }
}

export function cachePublishedLegalDocument(slug, doc, now = Date.now()) {
  if (typeof window === "undefined" || !validLegalDocument(doc)) return false;
  try {
    window.localStorage.setItem(legalCacheKey(slug), JSON.stringify({ slug, saved_at: now, doc }));
    return true;
  } catch {
    return false;
  }
}

// One layout for every company/legal page (About, Privacy, Terms, Refund, Cookie). Content is authored
// as Markdown, stored in the DB, and edited by the platform admin (Platform console → Legal Pages), so
// copy changes never require a redeploy. This page fetches the published doc from `/api/public/legal/:slug`
// and renders it premium: an auto table-of-contents from the `##` sections, brand-highlighted
// "Vidyapeeth360", and styled tables/notes. The `slug` prop (or the route param) selects the doc.
export default function LegalPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const [state, setState] = useState({ status: "loading", doc: null, source: null }); // loading | ok | notfound | error
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    let alive = true;
    setState({ status: "loading", doc: null, source: null });
    api.get(`/public/legal/${slug}`)
      .then(({ data }) => {
        if (!validLegalDocument(data)) throw new Error("Incomplete published legal document.");
        cachePublishedLegalDocument(slug, data);
        if (alive) setState({ status: "ok", doc: data, source: "published" });
      })
      .catch((error) => {
        if (!alive) return;
        const cached = readCachedLegalDocument(slug);
        if (cached) {
          setState({ status: "ok", doc: cached, source: "cached" });
          return;
        }
        if (LEGAL_FALLBACKS[slug]) {
          setState({ status: "ok", doc: LEGAL_FALLBACKS[slug], source: "bundled" });
          return;
        }
        setState({ status: error?.response?.status === 404 ? "notfound" : "error", doc: null, source: null });
      });
    return () => { alive = false; };
  }, [reloadKey, slug]);

  const doc = state.doc;
  const toc = useMemo(() => (doc?.body ? extractHeadings(doc.body) : []), [doc]);

  // PGS-4 handoff: an unknown/unpublished legal slug is a 404, not a silent homepage redirect —
  // the branded marketing 404 keeps the bad address visible and carries robots noindex.
  if (state.status === "notfound") return <MarketingNotFound />;

  const showToc = toc.length >= 3;
  const headTitle = doc?.title || LEGAL_TITLES[slug] || "Legal";
  const canonicalPath = LEGAL_PATHS[slug] || `/legal/${encodeURIComponent(slug)}`;
  const seo = LEGAL_SEO[slug] || {
    title: `${headTitle} | Vidyapeeth360`,
    description: `Read ${headTitle}, published by Vidyapeeth360.`,
  };
  const schema = {
    "@context": "https://schema.org",
    "@type": slug === "about" ? "AboutPage" : "WebPage",
    name: headTitle,
    description: seo.description,
    url: `${seoOrigin}${canonicalPath}`,
  };

  return (
    <MarketingMotion>
    <div className="min-h-screen bg-canvas text-ink flex flex-col landing-page">
      <MarketingSeo title={seo.title} description={seo.description} path={canonicalPath} schema={schema} />
      <MarketingTopBar />
      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-5 py-12 sm:py-16">
          {/* Header */}
          <div className="max-w-3xl">
            <h1 className="font-heading font-bold text-3xl sm:text-4xl text-ink tracking-tight"><BrandCopy>{headTitle}</BrandCopy></h1>
            {/* Legal-doc metadata belongs on legal docs — the About page is a company story, and
                "Effective date · Powered by <operator>" read as boilerplate leakage there. */}
            {state.status === "ok" && slug !== "about" && (
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-ink-subtle">
                {doc.effective_date && (
                  <span>{doc.is_fallback ? "Continuity copy: " : "Effective date: "}<span className="font-medium text-ink-2">{doc.effective_date}</span></span>
                )}
                {doc.powered_by && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-1 w-1 rounded-full bg-ink-subtle/50" aria-hidden="true" />
                    Powered by <span className="font-medium text-ink-2"><BrandCopy>{doc.powered_by}</BrandCopy></span>
                  </span>
                )}
              </div>
            )}
          </div>

          {state.status === "loading" && (
            <div className="mt-10 max-w-3xl space-y-3" aria-busy="true">
              <span className="sr-only" role="status">Loading…</span>
              {[...Array(8)].map((_, i) => <Skeleton key={i} className={`h-4 ${i % 3 === 0 ? "w-1/3" : "w-full"} rounded`} />)}
            </div>
          )}

          {state.status === "error" && (
            <div className="mt-10 max-w-md">
              <ErrorState title="Couldn’t load this page" desc="Please check your connection and try again."
                onRetry={() => setReloadKey((value) => value + 1)} retryLabel="Try again" />
            </div>
          )}

          {state.status === "ok" && (
            <>
              {state.source !== "published" && (
                <div className="mt-8 flex max-w-3xl flex-col gap-3 rounded-2xl border border-caution/30 bg-caution-soft px-4 py-4 text-sm leading-relaxed text-caution sm:flex-row sm:items-center sm:justify-between" role="status" data-testid="legal-continuity-notice">
                  <p>
                    <strong className="font-semibold">The published document is temporarily unavailable.</strong>{" "}
                    {state.source === "cached"
                      ? "You are viewing the most recent published copy saved on this device."
                      : `You are viewing bundled continuity copy ${LEGAL_FALLBACK_VERSION}. It does not supersede the current CMS-published document or an executed agreement.`}
                  </p>
                  <button type="button" onClick={() => setReloadKey((value) => value + 1)} className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-full border border-caution/35 bg-surface px-4 py-2 text-[13px] font-semibold text-ink transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring">
                    Try again
                  </button>
                </div>
              )}
              <div className={showToc ? "mt-10 lg:grid lg:grid-cols-[220px_1fr] lg:gap-12" : "mt-8"}>
              {showToc && (
                <nav aria-label="On this page" className="mb-8 hidden self-start lg:sticky lg:top-[128px] lg:block">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-subtle">On this page</p>
                  <ul className="space-y-1.5 border-l border-line">
                    {toc.map((t) => (
                      <li key={t.id}>
                        <a href={`#${t.id}`}
                          className="-ml-px block border-l-2 border-transparent py-1 pl-3 text-[13px] leading-snug text-ink-subtle transition-colors hover:border-l-orange hover:text-ink focus-visible:outline-none focus-visible:text-ink">
                          <BrandCopy>{t.label}</BrandCopy>
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}
              <div className="min-w-0 max-w-3xl">
                {/* About gets an editorial first paragraph — the mission line deserves lede sizing. */}
                <div className={slug === "about" ? "[&>div>p:first-of-type]:text-[19px] [&>div>p:first-of-type]:leading-relaxed" : undefined}>
                  <LegalMarkdown source={doc.body} />
                </div>
                {slug === "about" && founderNoteReady(FOUNDER_NOTE) && (
                  <section aria-labelledby="about-founder-h" className="mt-10 rounded-2xl border border-line bg-surface p-6 sm:p-7" data-testid="about-founder-note">
                    <h2 id="about-founder-h" className="font-heading font-bold text-xl text-ink tracking-tight">A note from our founder</h2>
                    {FOUNDER_NOTE.note.map((para) => (
                      <p key={para.slice(0, 40)} className="mt-3 text-[15px] leading-relaxed text-ink-2"><BrandCopy>{para}</BrandCopy></p>
                    ))}
                    {FOUNDER_NOTE.whyIndiaFirst && (
                      <p className="mt-3 text-[15px] leading-relaxed text-ink-2"><BrandCopy>{FOUNDER_NOTE.whyIndiaFirst}</BrandCopy></p>
                    )}
                    <p className="mt-4 text-sm font-semibold text-ink">— {FOUNDER_NOTE.name}, {FOUNDER_NOTE.role}</p>
                  </section>
                )}
                {slug === "about" && (
                  <section aria-labelledby="about-honesty-h" className="mt-10 rounded-2xl border border-line bg-surface p-6 sm:p-7" data-testid="about-honesty-commitments">
                    <h2 id="about-honesty-h" className="font-heading font-bold text-xl text-ink tracking-tight">What you will never see on this site</h2>
                    <p className="mt-2 text-[14px] leading-relaxed text-ink-2">
                      Every factual statement on our marketing site is checked against the shipped product
                      before it is published. That discipline means some familiar marketing furniture is
                      deliberately absent:
                    </p>
                    <ul className="mt-4 space-y-2.5">
                      {HONESTY_COMMITMENTS.map((line) => (
                        <li key={line} className="flex gap-2.5 text-[14.5px] leading-relaxed text-ink-2">
                          <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-4 text-[13px] leading-relaxed text-ink-subtle">
                      When we earn real customer proof, it will appear here with the school&rsquo;s
                      permission — not before.
                    </p>
                  </section>
                )}
                {slug === "about" && (
                  <div className="mt-10 rounded-2xl bg-royal p-6 text-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-grid opacity-20" aria-hidden="true" />
                    <div className="relative">
                      <h2 className="font-heading font-bold text-white text-xl">See it running, not described.</h2>
                      <p className="text-white/65 text-sm mt-1.5">Sign up free in minutes — we verify each new school before the workspace opens — or request a reviewed product walkthrough.</p>
                      <div className="mt-4 flex flex-wrap justify-center gap-2.5">
                        <Link to="/register" className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-white/90 focus:outline-none focus-visible:ring-2 ring-sky" data-testid="about-cta-register">{CTA_PRIMARY_LABEL}</Link>
                        <BookDemoLink bare contextLabel="Vidyapeeth360 company and product overview" placement="about_page" className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-white/40 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky" data-testid="about-cta-tour">Book a free demo</BookDemoLink>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              </div>
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
    </MarketingMotion>
  );
}
